/**
 * The monthly rate audit, as a detail-panel section.
 *
 * Stacie, item 3: "within the Active tab, can we prepare for regular (monthly) rate
 * auditing and make sure we have fields that capture last tested, test result,
 * tested commission seen, lowest tested negotiated rate seen, tested by."
 *
 * Shaped like `SupplierContactLog` on purpose — own request, three distinguishable
 * states, form below the history. Two child-log sections that behave differently is
 * how the third one gets it wrong. Not abstracted into a shared component yet:
 * they diverge on what they SHOW (drift and consecutive failures here; awaiting-reply
 * there), and a common wrapper would have to take both as options, which is more
 * shape than either needs.
 *
 * The thing this section exists to show is the DRIFT — agreed versus observed. The
 * server computes it so there is one definition of it, and so the browser never does
 * money arithmetic.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { fmtInstant } from '@/features/crm/lib/dates';
import { LogRateTestForm } from './LogRateTestForm';

export interface RateTest {
  id: string;
  testedAt: string;
  testedBy: string;
  gds: string | null;
  result: string;
  rateSeen: string | null;
  rateSeenCurrency: string | null;
  commissionSeenPct: string | null;
  notes: string | null;
}

export interface LastRateTest {
  at: string;
  by: string;
  gds: string | null;
  result: string;
  failing: boolean;
  rateSeen: string | null;
  rateSeenCurrency: string | null;
  commissionSeenPct: string | null;
  total: number;
  consecutiveFailures: number;
  daysSince: number | null;
}

interface RateTestsResponse {
  tests: RateTest[];
  total: number;
  truncated: boolean;
  last_test: LastRateTest | null;
  agreed: {
    rate: string | null;
    currency: string | null;
    commission_pct: string | null;
  };
  drift: { rate: string | null; commission_pct: string | null } | null;
}

/**
 * Human labels. Falls back to the raw value: the vocabulary is provisional, and a
 * result we have not labelled is still a recorded finding.
 */
const RESULT_LABELS: Record<string, string> = {
  as_agreed: 'As agreed',
  rate_differs: 'Rate differs',
  not_bookable: 'Not bookable',
  commission_differs: 'Commission differs',
  other: 'Other',
};

const GDS_LABELS: Record<string, string> = {
  sabre: 'Sabre',
  travelport: 'Travelport',
  worldspan: 'Worldspan',
};

function label(map: Record<string, string>, value: string | null): string {
  if (!value) return '—';
  return map[value] ?? value;
}

/**
 * A drift figure, signed and readable.
 *
 * `+21.00` reads as "we are paying $21 more than we agreed", which is the direction
 * that matters. Zero renders as "matches" rather than `0.00`, because the word is
 * what a reader is actually looking for and a zero has to be decoded.
 */
function driftLabel(drift: string | null, currency?: string | null): string {
  if (drift == null) return '—';
  const n = Number(drift);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return 'Matches';
  const sign = n > 0 ? '+' : '';
  return `${sign}${drift}${currency ? ` ${currency}` : ''}`;
}

export interface RateTestLogProps {
  agreementId: string;
  testId?: string;
  /**
   * Fired after an audit is logged, because logging one can MOVE THE AGREEMENT.
   * A passing test is what advances a loading agreement to active — Stacie,
   * 2026-08-31: "once it's as agreed, it shouldn't even show up on this loading
   * tab" — and without this the status change is invisible until someone
   * reloads the page by hand. That gap is why the workflow looked broken in the
   * session: the transition was fine, nothing asked the screen to look again.
   */
  onAgreementMayHaveChanged?: () => void;
}

export function RateTestLog({
  agreementId,
  testId = 'rate-test-log',
  onAgreementMayHaveChanged,
}: RateTestLogProps) {
  const [data, setData] = useState<RateTestsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiFetch<RateTestsResponse>(
      `/api/btp/rate-tests?agreement_id=${encodeURIComponent(agreementId)}`,
    )
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) {
          // An ERROR, not an empty list. An empty audit history reads as "this rate
          // has never been checked", which is a claim about our process; a failed
          // request is a claim about our uptime.
          setError(e instanceof ApiError ? e.message : String(e));
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agreementId]);

  useEffect(() => load(), [load]);

  const last = data?.last_test ?? null;

  return (
    <div data-testid={testId}>
      {loading && !data && (
        <p
          data-testid={`${testId}-loading`}
          style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}
        >
          Loading audit history…
        </p>
      )}

      {error && (
        <p
          data-testid={`${testId}-error`}
          style={{ fontSize: 12, color: 'var(--text-primary)', margin: 0 }}
        >
          Audit history isn&rsquo;t available right now — this is a loading or
          connection problem, not an unaudited rate.
        </p>
      )}

      {!loading && !error && data && data.total === 0 && (
        <p
          data-testid={`${testId}-empty`}
          style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}
        >
          No rate audit recorded yet. That is not the same as never tested —
          anything checked before this log existed was not captured.
        </p>
      )}

      {!error && data && last && (
        <>
          <dl
            className="detail-grid"
            data-testid={`${testId}-summary`}
            style={{ marginBottom: 12 }}
          >
            <div className="detail-field">
              <dt>Last tested</dt>
              {/* A true instant — fmtInstant, never fmtCalendarDate. */}
              <dd>{fmtInstant(last.at) ?? '—'}</dd>
            </div>
            <div className="detail-field">
              <dt>Result</dt>
              <dd>
                <span
                  className={
                    'badge badge-no-dot ' +
                    (last.failing ? 'badge-amber' : 'badge-green')
                  }
                >
                  {label(RESULT_LABELS, last.result)}
                </span>
              </dd>
            </div>
            <div className="detail-field">
              <dt>Tested by</dt>
              <dd>{last.by}</dd>
            </div>
            <div className="detail-field">
              <dt>GDS</dt>
              <dd>{label(GDS_LABELS, last.gds)}</dd>
            </div>
            {/* The comparison, side by side. Showing the agreed figure next to the
                observed one is what makes the drift legible without arithmetic. */}
            <div className="detail-field">
              <dt>Rate agreed / seen</dt>
              <dd>
                {data.agreed.rate ?? '—'} / {last.rateSeen ?? '—'}
              </dd>
            </div>
            <div className="detail-field">
              <dt>Rate drift</dt>
              <dd data-testid={`${testId}-rate-drift`}>
                {driftLabel(data.drift?.rate ?? null, data.agreed.currency)}
              </dd>
            </div>
            <div className="detail-field">
              <dt>Commission agreed / seen</dt>
              <dd>
                {data.agreed.commission_pct ?? '—'} /{' '}
                {last.commissionSeenPct ?? '—'}
              </dd>
            </div>
            {/* Only when there IS a run of failures. A permanent "0 consecutive"
                is noise, and noise is what makes people stop reading the badge
                that matters. */}
            {last.consecutiveFailures > 1 && (
              <div className="detail-field">
                <dt>Failing since</dt>
                <dd data-testid={`${testId}-streak`}>
                  <span className="badge badge-amber badge-no-dot">
                    {last.consecutiveFailures} audits running
                  </span>
                </dd>
              </div>
            )}
            {last.daysSince != null && last.daysSince > 45 && (
              <div className="detail-field">
                <dt>Overdue</dt>
                <dd data-testid={`${testId}-overdue`}>
                  {/* The cadence Stacie asked to prepare for is monthly, so past
                      45 days the audit is late rather than merely old. Shown only
                      when true, for the same reason as the streak. */}
                  <span className="badge badge-amber badge-no-dot">
                    {last.daysSince} days since last test
                  </span>
                </dd>
              </div>
            )}
          </dl>

          <table className="detail-table" data-testid={`${testId}-table`}>
            <thead>
              <tr>
                <th>Tested</th>
                <th>Result</th>
                <th>GDS</th>
                <th className="right">Rate seen</th>
                <th className="right">Comm. seen</th>
                <th>By</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {data.tests.map((t) => (
                <tr key={t.id}>
                  <td>{fmtInstant(t.testedAt) ?? '—'}</td>
                  <td>{label(RESULT_LABELS, t.result)}</td>
                  <td>{label(GDS_LABELS, t.gds)}</td>
                  <td className="right">
                    {t.rateSeen
                      ? `${t.rateSeen}${t.rateSeenCurrency ? ` ${t.rateSeenCurrency}` : ''}`
                      : '—'}
                  </td>
                  <td className="right">
                    {t.commissionSeenPct ? `${t.commissionSeenPct}%` : '—'}
                  </td>
                  <td>{t.testedBy}</td>
                  <td>{t.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {data.truncated && (
            <p
              data-testid={`${testId}-truncated`}
              style={{
                fontSize: 11,
                marginTop: 8,
                color: 'var(--text-primary)',
              }}
            >
              Showing the {data.tests.length.toLocaleString()} most recent of{' '}
              {data.total.toLocaleString()} recorded audits.
            </p>
          )}
        </>
      )}

      {/* Rendered even when the READ failed: being unable to show the history is no
          reason to stop someone recording the audit they just ran. Hidden only while
          the first load is in flight. */}
      {!loading && (
        <LogRateTestForm
          agreementId={agreementId}
          onSaved={() => {
            load();
            onAgreementMayHaveChanged?.();
          }}
          testId={`${testId}-add`}
        />
      )}
    </div>
  );
}
